import { css } from '@emotion/react';
import { spacing, color } from '@uniquegood/realworld-studio-design';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';

import { useRequest, useRestrictProjectRoute, usePromotionModal } from '@/hooks';
import { secondsToMinutes } from '@/utils';

import { container960style } from '@/styles/containerStyles';
import LoadingSpinner from '@/components/LoadingSpinner';
import KpiCard from './components/KpiCard';
// import ScrollNavigation from './components/ScrollNavigation';
import MissionCompletionSection from './components/MissionCompletionSection';
import PlayTimeSection from './components/PlayTimeSection';
import PlayTimeLimitSection from './components/PlayTimeLimitSection';
import TrendSection from './components/TrendSection';
import '@styles/flatpickr.css';
import { PageViewEventName, PromotionTriggerLocation, track } from '@/track';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import MissionCompletionLimitSection from './components/MissionCompletionLimitSection';
import FloatingNavigation, { FloatingNavigationProps } from '@/components/FloatingNavigation';
import UserVariableSection from './components/UserVariableSection';

export default function Dashboard() {
  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_dashboard });
  }, []);

  const { appId, projectId } = useParams<AppParam>();
  const { openPromotionModal } = usePromotionModal();

  const path = `/apps/${appId}/projects/${projectId}/statistics`;
  const { data: app } = useRequest<RealWorldApp>(`/apps/${appId}`);
  const redirectTo = useRestrictProjectRoute();
  const { plan, limits } = usePlanLimits();
  const history = useHistory();

  const {
    data: {
      data: accumulateData
    } = {} as CommonResponseModel<ProjectAccumulateStatisticsResponseModel>
  } = useRequest<CommonResponseModel<ProjectAccumulateStatisticsResponseModel>>(
    `${path}/accumulate`
  );

  // 요금제별 데이터 제한 체크
  const canDownload = limits.dataDownload;
  const isRealworldOriginalChannel =
    appId?.toLowerCase() === 'realworld' || app?.channelType === 'Realworld';

  const floatingNavigationItems = useMemo<FloatingNavigationProps['items']>(() => {
    const items: FloatingNavigationProps['items'] = [
      { content: '기간별 현황', fragment: 'trend' },
      { content: '미션별 이탈율', fragment: 'mission' },
      { content: '플레이 시간', fragment: 'playtime' }
    ];

    if (isRealworldOriginalChannel) {
      items.push({ content: '유저 입력 변수', fragment: 'variables' });
    }

    return items;
  }, [isRealworldOriginalChannel]);

  // 업그레이드 모달 열기
  const handleUpgradeClick = (option?: 'mission' | 'playtime') => {
    openPromotionModal({
      recommendedPlan: plan === 'Free' ? 'Pro' : 'Enterprise',
      triggerLocation:
        option === 'mission'
          ? PromotionTriggerLocation.dashboard_mission_completion
          : PromotionTriggerLocation.dashboard_play_time
    });
  };

  const handleCommentClick = () => {
    history.push(`/apps/${appId}/projects/${projectId}/comments`);
  };

  if (!app || !accumulateData || redirectTo === undefined) return <LoadingSpinner />;

  if (typeof redirectTo === 'string') return <Redirect to={redirectTo} />;

  return (
    <>
      <FloatingNavigation
        items={floatingNavigationItems}
        cssStyle={FloatingNavigationBarStyle}
        offset={24}
      />
      <div css={container960style}>
        {/* KPI 보드 섹션 */}
        <div css={KpiGridLayout}>
          <KpiCard
            title={{ text: '누적 플레이 인원' }}
            value={accumulateData?.playerCount ?? 0}
            color={color.palette_primary_purple_100}
            icon="user_friends_solid"
          />
          <KpiCard
            title={{ text: '누적 완료 인원', icon: 'user_solid' }}
            value={accumulateData?.completedPlayerCount ?? 0}
            color={color.palette_primary_purple_100}
            icon="user_solid"
          />
          <KpiCard
            title={{ text: '평균 완료율' }}
            value={`${Math.round(accumulateData?.averageCompleteRate ?? 0)}%`}
            color={color.palette_primary_purple_100}
            icon="check_solid"
            isRestricted={plan === 'Free'}
            onRestrictedClick={handleUpgradeClick}
            restrictedValue="??%"
          />
          <KpiCard
            title={{ text: '평균 플레이 시간' }}
            value={`${secondsToMinutes(accumulateData?.averagePlayTimeSeconds,0)}분`}
            color={color.palette_primary_purple_100}
            icon="clock_solid"
            isRestricted={plan === 'Free' || plan === 'Pro' || plan === 'Teacher'}
            onRestrictedClick={handleUpgradeClick}
            restrictedValue="??분"
          />
          <KpiCard
            title={{ text: '누적 후기 수', icon: 'chevron_right_regular' }}
            value={`${accumulateData?.reviewCount ?? 0}개`}
            color={color.palette_primary_purple_100}
            icon="comment_dots_solid"
            valueIcon="chevron_right_regular"
            onClick={handleCommentClick}
          />
        </div>

        {/* 기간별 현황 섹션 */}
        <section id="trend">
          <TrendSection
            appId={appId}
            projectId={projectId ?? ''}
            plan={plan}
            canDownload={canDownload}
          />
        </section>

        {/* 미션별 완료율 섹션 */}
        <section id="mission">
          {plan === 'Free' ? (
            <MissionCompletionLimitSection onUpgradeClick={() => handleUpgradeClick('mission')} />
          ) : (
            <MissionCompletionSection
              appId={appId}
              projectId={projectId}
              canDownload={canDownload}
            />
          )}
        </section>

        {/* 플레이 시간 섹션 */}
        <section id="playtime">
          {plan === 'Free' || plan === 'Pro' || plan === 'Teacher' ? (
            <PlayTimeLimitSection onUpgradeClick={() => handleUpgradeClick('playtime')} />
          ) : (
            <PlayTimeSection appId={appId} projectId={projectId} canDownload={canDownload} />
          )}
        </section>

        {isRealworldOriginalChannel && (
          <section id="variables">
            <UserVariableSection appId={appId} projectId={projectId} canDownload={canDownload} />
          </section>
        )}
      </div>
    </>
  );
}

const FloatingNavigationBarStyle = css`
  position: fixed;
  left: 1300px;
  width: 160px;

  @media (max-width: 1440px) {
    display: none;
  }
`;

const KpiGridLayout = css`
  display: flex;
  gap: ${spacing.margin.large};
  margin-bottom: ${spacing.margin.xlarge2};

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;
